import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { openActionUrl } from '@/lib/openActionUrl';
import { type Notification } from '@/services/notificationsApi';
import NotificationIcon from './NotificationIcon';

// Delay (ms) used to tell a single click apart from a double click. A single
// click only acts after this window passes with no second click; a double click
// cancels the pending single-click action. Keeps single-click navigation while
// double-click opens the detail modal (T863).
const CLICK_DELAY_MS = 220;

interface NotificationItemProps {
  notification: Notification;
  onMarkRead: (id: string) => void;
  onOpen: (notification: Notification) => void;
  /**
   * T2770 — zapre spustni seznam zvonca, ko enojni klik navigira na
   * `action_url`; sicer seznam ostane odprt čez ciljno stran. Na strani
   * /notifications seznama ni, zato je neobvezen.
   */
  onClose?: () => void;
}

function formatRelativeTime(dateStr: string, locale: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHr = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHr / 24);

  if (diffSec < 60) return `${diffSec}s`;
  if (diffMin < 60) return `${diffMin}m`;
  if (diffHr < 24) return `${diffHr}h`;
  if (diffDay < 7) return `${diffDay}d`;
  return date.toLocaleDateString(locale);
}

export default function NotificationItem({ notification, onMarkRead, onOpen, onClose }: NotificationItemProps) {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const clickTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (clickTimer.current) clearTimeout(clickTimer.current);
    };
  }, []);

  // Single click: defer the existing mark-read + navigate so a second click
  // (double click) can cancel it and open the modal instead.
  const handleClick = () => {
    if (clickTimer.current) return;
    clickTimer.current = setTimeout(() => {
      clickTimer.current = null;
      onMarkRead(notification.id);
      if (notification.action_url) {
        openActionUrl(notification.action_url, navigate);
        onClose?.();
      }
    }, CLICK_DELAY_MS);
  };

  // Double click: cancel the pending single-click action and open the detail modal.
  const handleDoubleClick = () => {
    if (clickTimer.current) {
      clearTimeout(clickTimer.current);
      clickTimer.current = null;
    }
    onMarkRead(notification.id);
    onOpen(notification);
  };

  return (
    <button
      onClick={handleClick}
      onDoubleClick={handleDoubleClick}
      title={t('notifications.double_click_hint')}
      className={cn(
        'w-full flex items-start gap-3 px-4 py-3 text-left select-none hover:brightness-95 transition-colors relative',
        notification.is_read ? 'bg-[var(--bp-surface)]' : 'bg-[var(--bp-accent-soft)]'
      )}
    >
      {/* Icon */}
      <div className="shrink-0 mt-0.5 text-[var(--bp-text-3)]">
        <NotificationIcon type={notification.type} className="h-5 w-5" />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0 pr-3">
        <p
          className={cn(
            'text-body leading-snug truncate',
            notification.is_read ? 'font-normal text-[var(--bp-text-2)]' : 'font-semibold text-[var(--bp-text-1)]'
          )}
        >
          {notification.title}
        </p>
        {notification.body && (
          <p className="text-body text-[var(--bp-text-3)] mt-0.5 line-clamp-2">{notification.body}</p>
        )}
        <p className="text-caption text-[var(--bp-mute)] mt-1">{formatRelativeTime(notification.created_at, i18n.language)}</p>
      </div>

      {/* Unread dot */}
      {!notification.is_read && (
        <span className="absolute top-3 right-3 h-2 w-2 rounded-full bg-blue-500 shrink-0" />
      )}
    </button>
  );
}
